/** 匿名化(個人情報の消去)。退職済みで、まだ匿名化していない人だけを出す。
 *
 * 匿名化は行を消さない。氏名・メール・社員番号を伏せ、履歴(申請・タスク・社外依頼・
 * 送信記録・貸与・お知らせ・監査)の文字列も同じトランザクションで置き換える。
 * 物理削除は履歴が1件でも残っていればできない(サーバの案内をそのまま出す)。
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Modal, Space, Table, Tag, Typography, message } from "antd";
import type { ColumnsType } from "antd/es/table";
import { Link } from "react-router-dom";

import type { AnonymizeCandidate } from "../../lib/api";
import { jst } from "../../lib/format";
import { useApi } from "../../lib/useApi";
import { errorText, useHrPerms } from "./hooks";

const { Paragraph, Text } = Typography;

export function AnonymizePage() {
  const api = useApi();
  const qc = useQueryClient();
  const { canAdmin } = useHrPerms();

  const query = useQuery({
    queryKey: ["hr", "anonymize-candidates"],
    queryFn: api.anonymizeCandidates,
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["hr"] });
    void qc.invalidateQueries({ queryKey: ["audit"] });
    void qc.invalidateQueries({ queryKey: ["notifications"] });
  };

  const anonymize = useMutation({
    mutationFn: (id: number) => api.anonymizeEmployee(id),
    onSuccess: (res) => {
      void message.success(`匿名化した(履歴の置換 ${res.history_rows} 行)`);
      refresh();
    },
    onError: (e: unknown) => void message.error(errorText(e)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteEmployee(id),
    onSuccess: (res) => {
      void message.success(`削除した(${res.emp_code})`);
      refresh();
    },
    onError: (e: unknown) => {
      // 409 の案内(どの履歴が何件残っているか)はサーバの文をそのまま見せる
      Modal.error({ title: "削除できない", content: errorText(e), okText: "閉じる" });
    },
  });

  const runJob = useMutation({
    mutationFn: () => api.runRetention(),
    onSuccess: (res) => {
      // **保留が出ても匿名化した人数は出す**(fix2 レビュー 新規 低)。
      // ジョブは保留した人だけを飛ばして残りは匿名化するので、理由だけ見せると
      // 「何も起きなかった」ように読めてしまう
      if (res.anonymized > 0 && res.skipped) {
        void message.success(`${res.anonymized} 名を匿名化した / ${res.skipped}`);
      } else if (res.skipped) void message.info(res.skipped);
      else if (res.anonymized === 0) void message.info("対象は無かった");
      else void message.success(`${res.anonymized} 名を匿名化した`);
      refresh();
    },
    onError: (e: unknown) => void message.error(errorText(e)),
  });

  const days = query.data?.retention_days ?? 0;

  const confirmAnonymize = (row: AnonymizeCandidate) => {
    Modal.confirm({
      title: `${row.name}(${row.emp_code})を匿名化する`,
      okText: "匿名化する",
      okButtonProps: { danger: true },
      cancelText: "やめる",
      content: (
        <Space direction="vertical" size={4}>
          <span>氏名・メールアドレス・社員番号・所属・役職・上長・契約形態を消す。</span>
          <span>申請・タスク・貸与・監査の行は残し、その中の文字列も置き換える。</span>
          <Text type="danger">元には戻せない。</Text>
        </Space>
      ),
      onOk: () => anonymize.mutateAsync(row.id),
    });
  };

  const confirmDelete = (row: AnonymizeCandidate) => {
    Modal.confirm({
      title: `${row.name}(${row.emp_code})を削除する`,
      okText: "削除する",
      okButtonProps: { danger: true },
      cancelText: "やめる",
      content: (
        <Space direction="vertical" size={4}>
          <span>行そのものを消す。履歴が1件でも残っているときは消せない。</span>
          <Text type="secondary">退職済みで履歴があるなら匿名化を使う。</Text>
        </Space>
      ),
      onOk: () => remove.mutateAsync(row.id),
    });
  };

  const columns: ColumnsType<AnonymizeCandidate> = [
    { title: "社員番号", dataIndex: "emp_code", width: 110 },
    {
      title: "氏名",
      dataIndex: "name",
      width: 160,
      render: (v: string, row) => <Link to={`/hr/employees/${row.id}`}>{v}</Link>,
    },
    { title: "メールアドレス", dataIndex: "email", width: 240 },
    {
      title: "所属",
      width: 160,
      dataIndex: "department",
      render: (v: string) => v || <Text type="secondary">—</Text>,
    },
    {
      title: "退職日",
      dataIndex: "left_at",
      width: 120,
      render: (v: string | null) => v || "—",
    },
    {
      title: "退職手続きの完了",
      dataIndex: "offboard_done_at",
      width: 320,
      render: (v: string | null, row) =>
        v ? (
          <Space size={4}>
            <span>{jst(v)}</span>
            <Text type="secondary">({row.days_elapsed} 日前)</Text>
          </Space>
        ) : (
          <Text type="secondary">—</Text>
        ),
    },
    {
      title: "保有期間",
      width: 320,
      render: (_: unknown, row) =>
        row.blocked ? (
          <Text type="secondary">{row.blocked}</Text>
        ) : row.retention_due ? (
          <Tag color="red">経過(次のジョブで自動匿名化)</Tag>
        ) : days > 0 ? (
          <Tag color="blue">期間内</Tag>
        ) : (
          <Text type="secondary">自動匿名化は停止中</Text>
        ),
    },
    {
      title: "",
      width: 180,
      render: (_: unknown, row) => (
        <Space>
          <Button
            size="small"
            danger
            disabled={!canAdmin}
            onClick={() => confirmAnonymize(row)}
          >
            匿名化
          </Button>
          <Button size="small" disabled={!canAdmin} onClick={() => confirmDelete(row)}>
            削除
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>匿名化(個人情報の消去)</Typography.Title>
      <Paragraph type="secondary" style={{ maxWidth: 900 }}>
        匿名化できるのは<Text strong>退職済み</Text>の人だけ。氏名・メールアドレス・社員番号を
        伏せ、履歴(申請・タスク・社外依頼・送信記録・貸与・お知らせ・監査)の文字列も同じ
        トランザクションで置き換える。行は消さないので件数と流れは追える。
        <Text strong>元には戻せない。</Text>
      </Paragraph>

      <Alert
        type={days > 0 ? "info" : "warning"}
        showIcon
        style={{ marginBottom: 16 }}
        message={
          days > 0
            ? `保有期間: 退職手続きの完了から ${days} 日で自動匿名化する`
            : "保有期間: 0 日(自動匿名化は停止中)"
        }
        description={
          <Space direction="vertical" size={4}>
            <span>
              日数は <Link to="/admin/settings">管理 &gt; 設定</Link> の{" "}
              <Text code>retention</Text>(days_after_offboard)で変える。
            </span>
            <Text type="secondary">
              退職手続きを通っていない人と、手続きのタスクが残っている人は自動では触らない。
            </Text>
          </Space>
        }
        action={
          canAdmin ? (
            <Button size="small" loading={runJob.isPending} onClick={() => runJob.mutate()}>
              保有期間ジョブを今すぐ実行
            </Button>
          ) : undefined
        }
      />

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="一覧を取得できなかった"
          description={errorText(query.error)}
        />
      )}

      <Card size="small" title={`退職済み・未匿名化 ${query.data?.total ?? 0} 名`}>
        <Table<AnonymizeCandidate>
          scroll={{ x: "max-content" }}
          rowKey="id"
          size="small"
          columns={columns}
          dataSource={query.data?.items ?? []}
          loading={query.isFetching}
          pagination={false}
          locale={{ emptyText: "匿名化していない退職者は無い" }}
        />
      </Card>
    </div>
  );
}
